import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore'
import { db } from '../firebase/config'
import { Video, PlayCircle, Loader, HelpCircle, ChevronUp, Share2, Check, Search } from 'lucide-react'
import toast from 'react-hot-toast'

const HelpCenter = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [videoGuides, setVideoGuides] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedGuide, setSelectedGuide] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [copiedGuideId, setCopiedGuideId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const categories = ['All', 'Booking', 'Gift Card', 'Payment', 'General', 'Other']

  useEffect(() => {
    fetchVideoGuides()
  }, [])

  // Check for guide ID in URL and auto-select it
  useEffect(() => {
    const guideId = searchParams.get('guide')
    if (guideId && videoGuides.length > 0) {
      const guide = videoGuides.find(g => g.id === guideId)
      if (guide) {
        setSelectedGuide(guide)
        // Scroll to video after a short delay
        setTimeout(() => {
          const videoSection = document.getElementById('video-section')
          if (videoSection) {
            videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 300)
      }
    }
  }, [searchParams, videoGuides])

  const fetchVideoGuides = async () => {
    try {
      setLoading(true)
      const q = query(
        collection(db, 'videoGuides'),
        where('isActive', '==', true),
        orderBy('order', 'asc')
      )
      const snapshot = await getDocs(q)
      const guides = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setVideoGuides(guides)
    } catch (error) {
      console.error('Error fetching video guides:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredGuides = (selectedCategory === 'All' 
    ? videoGuides 
    : videoGuides.filter(guide => guide.category === selectedCategory)
  ).filter(guide => {
    const q = (searchQuery || '').trim().toLowerCase()
    if (!q) return true
    const match = (s) => (s || '').toLowerCase().includes(q)
    return match(guide.title) || match(guide.description) || match(guide.category)
  })

  const handleGuideClick = (guide) => {
    if (selectedGuide?.id === guide.id) {
      // If clicking the same guide, close it
      setSelectedGuide(null)
      setSearchParams({}) // Clear URL parameter
    } else {
      // Otherwise, open the new guide
      setSelectedGuide(guide)
      // Update URL with guide ID
      setSearchParams({ guide: guide.id })
      // Scroll to video section smoothly
      setTimeout(() => {
        const videoSection = document.getElementById('video-section')
        if (videoSection) {
          videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }

  const handleCopyUrl = async (e, guide) => {
    e.stopPropagation() // Prevent guide card click
    const url = `${window.location.origin}/help-center?guide=${guide.id}`
    
    try {
      await navigator.clipboard.writeText(url)
      setCopiedGuideId(guide.id)
      toast.success('Link copied to clipboard!', {
        icon: '🔗',
        style: {
          background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
          color: '#ffffff',
        },
      })
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedGuideId(null), 2000)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = url
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        setCopiedGuideId(guide.id)
        toast.success('Link copied to clipboard!', {
          icon: '🔗',
          style: {
            background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
            color: '#ffffff',
          },
        })
        setTimeout(() => setCopiedGuideId(null), 2000)
      } catch (fallbackErr) {
        toast.error('Failed to copy link')
      }
      document.body.removeChild(textArea)
    }
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50 min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50 pb-20">
      {/* Hero Section - facials1 background */}
      <div
        className="relative text-white py-16 md:py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/media/images/services/facials1.jpg)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <HelpCircle className="w-16 h-16 mx-auto mb-4 drop-shadow-md" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 drop-shadow-lg">Help Center</h1>
          <p className="text-lg md:text-xl text-white/95 max-w-2xl mx-auto drop-shadow-md">
            Step-by-step video guides to help you navigate our services
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search + Category Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-2xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guides..."
              aria-label="Search video guides"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-purple-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400"
            />
          </div>
          <div className="w-full sm:w-44 shrink-0">
            <label htmlFor="help-category" className="sr-only">Filter by category</label>
            <select
              id="help-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full appearance-none py-2.5 pl-4 pr-10 rounded-xl border-2 border-purple-200 bg-white text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400 cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23934eea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25rem'
              }}
            >
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Result count when filtering */}
        {(searchQuery.trim() || selectedCategory !== 'All') && filteredGuides.length > 0 && (
          <p className="text-sm text-gray-500 mb-3 text-center sm:text-left">
            Showing {filteredGuides.length} guide{filteredGuides.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Video Guides List - compact cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {filteredGuides.length === 0 ? (
            <div className="col-span-full text-center py-8">
              <Video className="w-10 h-10 mx-auto mb-2 text-purple-300" />
              <p className="text-sm text-gray-600">
                {searchQuery.trim() || selectedCategory !== 'All'
                  ? 'No video guides match your search or category.'
                  : 'No video guides available.'}
              </p>
            </div>
          ) : (
            filteredGuides.map((guide) => (
              <div
                key={guide.id}
                className={`bg-white rounded-md shadow-sm hover:shadow transition-all duration-200 overflow-hidden border ${
                  selectedGuide?.id === guide.id
                    ? 'border-purple-500 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-purple-100'
                }`}
              >
                <button
                  onClick={() => handleGuideClick(guide)}
                  className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1 rounded-md"
                >
                  <div className="px-2.5 py-2 flex items-center gap-2">
                    <div className={`flex-shrink-0 w-7 h-7 rounded flex items-center justify-center ${
                      selectedGuide?.id === guide.id
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                        : 'bg-gradient-to-r from-purple-400 to-pink-400'
                    }`}>
                      <PlayCircle className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-semibold text-gray-800 line-clamp-1">
                        {guide.title}
                      </h3>
                      {guide.description && (
                        <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                          {guide.description}
                        </p>
                      )}
                      <div className="mt-1 flex items-center justify-between">
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700">
                          {guide.category || 'General'}
                        </span>
                        <span className="text-[10px] font-medium text-purple-600">
                          Click to watch
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
                <div className="px-2.5 pb-1.5 pt-0 border-t border-gray-100">
                  <button
                    onClick={(e) => handleCopyUrl(e, guide)}
                    className="flex items-center justify-center gap-1 text-[11px] text-gray-500 hover:text-purple-600 transition-colors w-full py-1 rounded hover:bg-purple-50/50"
                    title="Copy shareable link"
                  >
                    {copiedGuideId === guide.id ? (
                      <>
                        <Check className="w-3 h-3 text-purple-600" />
                        <span className="text-purple-600 font-medium">Copied</span>
                      </>
                    ) : (
                      <>
                        <Share2 className="w-3 h-3" />
                        <span>Share</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Selected Video Section */}
        {selectedGuide && (
          <div id="video-section" className="mb-8">
            <div className="bg-white rounded-xl shadow-2xl border-2 border-purple-500 overflow-hidden">
              {/* Video Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <PlayCircle className="w-6 h-6" />
                  <h2 className="text-xl md:text-2xl font-bold">{selectedGuide.title}</h2>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Copy URL Button */}
                  <button
                    onClick={(e) => handleCopyUrl(e, selectedGuide)}
                    className="flex items-center space-x-2 text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
                    title="Copy shareable link"
                  >
                    {copiedGuideId === selectedGuide.id ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span className="text-sm font-medium">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Share2 className="w-5 h-5" />
                        <span className="text-sm font-medium">Share</span>
                      </>
                    )}
                  </button>
                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setSelectedGuide(null)
                      setSearchParams({}) // Clear URL parameter
                    }}
                    className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
                    aria-label="Close video"
                  >
                    <ChevronUp className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Video Embed */}
              <div className="p-4 md:p-6">
                {selectedGuide.embedUrl ? (
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-900">
                    <iframe
                      className="w-full h-full"
                      src={selectedGuide.embedUrl}
                      title={selectedGuide.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      frameBorder="0"
                    />
                  </div>
                ) : selectedGuide.youtubeVideoId ? (
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-900">
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${selectedGuide.youtubeVideoId}`}
                      title={selectedGuide.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      frameBorder="0"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full rounded-lg bg-gray-200 flex items-center justify-center">
                    <p className="text-gray-600">Video not available</p>
                  </div>
                )}

                {/* Video Description */}
                {selectedGuide.description && (
                  <div className="mt-4 p-4 bg-purple-50/80 rounded-lg border border-purple-100">
                    <p className="text-gray-700">{selectedGuide.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* All Guides List (shown when video is selected) - same compact cards */}
        {selectedGuide && (
          <div className="mt-6">
            <h3 className="text-base font-bold text-gray-800 mb-2">More Guides</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredGuides
                .filter(guide => guide.id !== selectedGuide.id)
                .map((guide) => (
                  <div
                    key={guide.id}
                    className="bg-white rounded-md shadow-sm hover:shadow transition-all duration-200 overflow-hidden border border-gray-200 hover:border-purple-100"
                  >
                    <button
                      onClick={() => handleGuideClick(guide)}
                      className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1 rounded-md"
                    >
                      <div className="px-2.5 py-2 flex items-center gap-2">
                        <div className="flex-shrink-0 w-7 h-7 rounded flex items-center justify-center bg-gradient-to-r from-purple-400 to-pink-400">
                          <PlayCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs font-semibold text-gray-800 line-clamp-1">
                            {guide.title}
                          </h3>
                          {guide.description && (
                            <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                              {guide.description}
                            </p>
                          )}
                          <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700">
                            {guide.category || 'General'}
                          </span>
                        </div>
                      </div>
                    </button>
                    <div className="px-2.5 pb-1.5 pt-0 border-t border-gray-100">
                      <button
                        onClick={(e) => handleCopyUrl(e, guide)}
                        className="flex items-center justify-center gap-1 text-[11px] text-gray-500 hover:text-purple-600 transition-colors w-full py-1 rounded hover:bg-purple-50/50"
                        title="Copy shareable link"
                      >
                        {copiedGuideId === guide.id ? (
                          <>
                            <Check className="w-3 h-3 text-purple-600" />
                            <span className="text-purple-600 font-medium">Copied</span>
                          </>
                        ) : (
                          <>
                            <Share2 className="w-3 h-3" />
                            <span>Share</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HelpCenter

